import { observable, computed, makeObservable } from 'mobx'

import uniq from 'lodash/uniq'
import flatMap from 'lodash/flatMap'
import orderBy from 'lodash/orderBy'
import { required, func } from 'validx'
import compact from 'lodash/compact'
import { isNotNil } from 'utils/object-util'
import { DocumentDTO } from './dtos/document'
import { GetCurrentMember } from 'iam/SessionStore'
import { Model } from 'infra/model'
import { maybeParseDate, DateZero } from 'utils/date-util'
import { PrimeDocumentTag, GetDocumentTag } from 'documents/DocumentTagStore'
import {
  GetAccessesForDocument,
  PrimeDocumentAccess,
} from 'documents/DocumentAccessStore'
import {
  GetVersionsForDocument,
  PrimeDocumentVersion,
} from 'documents/DocumentVersionStore'
import { PrimeMember } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import { Id, Nullable } from 'types/definitions'
import once from 'lodash/once'
import DocumentVersion from './DocumentVersion'
import DocumentAccess from './DocumentAccess'
import DocumentTag from './DocumentTag'
import Workspace from 'iam/models/Workspace'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import DocumentType, { DocumentTypeField } from './DocumentType'
import { PrimeDocumentType } from './DocumentTypeStore'

export default class Document extends Model<DocumentDTO, Document> {
  static validationRules = {
    title: [
      required('Title is required'),
      func((opts) => opts.value.length >= 2, 'Must be more than 2 characters.'),
    ],
  }

  @observable id = Id.none
  @observable workspaceId = Id.none
  @observable title = ''
  @observable description = ''
  @observable archived = false
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable uploadProgress: Nullable<UploadProgress> = null
  @observable readonly owner = Member.none
  @observable readonly workspace = Workspace.none
  @observable canEdit = false
  @observable ephemeral = false
  @observable documentType: Nullable<DocumentType> = null

  // Should be private but `parse` does not work with that.
  @observable _tagIds: Array<string> = []

  static get none() {
    return lazyNone()
  }

  constructor(
    private primeMember: PrimeMember,
    private getCurrentMember: GetCurrentMember,
    private primeDocumentTag: PrimeDocumentTag,
    private primeDocumentAccess: PrimeDocumentAccess,
    private primeDocumentVersion: PrimeDocumentVersion,
    private primeDocumentType: PrimeDocumentType,
    private getDocumentTag: GetDocumentTag,
    private getAccessesForDocument: GetAccessesForDocument,
    private getVersionsForDocument: GetVersionsForDocument,
    private primeWorkspace: PrimeWorkspace,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get type() {
    return this.documentType
  }

  @computed
  get tags() {
    return orderBy<DocumentTag>(
      compact(this._tagIds.map(this.getDocumentTag)),
      'dateCreated',
      'desc',
    )
  }

  @computed
  get accesses() {
    const accesses = this.getAccessesForDocument(this.id)
    return orderBy<DocumentAccess>(accesses, 'dateCreated', 'desc')
  }

  @computed
  get versions() {
    const versions = this.getVersionsForDocument(this.id)
    return orderBy<DocumentVersion>(versions, 'dateCreated', 'desc')
  }

  @computed
  get latestVersion() {
    if (this.versions.length > 0) {
      return this.versions[0]
    }
  }

  @computed
  get downloadable() {
    return (
      !this.archived &&
      this.latestVersion &&
      this.latestVersion.state === 'Downloadable'
    )
  }

  @computed
  get latestVersionUnpurged() {
    return this.versions.find((version) => !version.purged)
  }

  @computed
  get isPurged() {
    return this.versions.length > 0
      ? !this.versions.some((version) => !version.purged)
      : false
  }

  @computed
  get collaborators() {
    return uniq(flatMap(this.versions, (version) => version.author)).filter(
      Boolean,
    )
  }

  @computed
  get fileType() {
    return this.latestVersion?.files[0]?.fileName.split('.').pop() ?? 'file'
  }

  @computed
  get canManageAccess() {
    const currentUser = this.getCurrentMember()
    return this.owner.user.id === currentUser.user.id
  }

  @computed
  get extractionState() {
    return this.latestVersion?.extractionState
  }

  @computed
  get documentTypeFields() {
    const accumulator: Record<string, DocumentTypeField> = {}

    if (!this.documentType?.fields) {
      return accumulator
    }

    reduceDocumentTypeFields(this.documentType.fields, accumulator)

    return accumulator
  }

  @computed
  get fields() {
    if (!this.latestVersion?.fields) {
      return []
    }

    return this.latestVersion.fields
      .map((field): DocumentField | null => {
        const fieldType = this.documentTypeFields[field.id]

        if (!isNotNil(fieldType)) {
          return null
        }

        const partialDocumentField = {
          id: field.id,
          schemaId: field.schemaId,
          fileId: field.fileId,
          path: field.path,
          corrected:
            isNotNil(field?.correctedValue) &&
            field?.extractedValue !== field?.correctedValue,
        }

        const value = field?.correctedValue || field?.extractedValue

        switch (fieldType.type) {
          case 'BOOL':
            return {
              ...partialDocumentField,
              type: fieldType.type,
              value: typeof value !== 'boolean' ? false : value,
            }
          case 'TEXT':
            return {
              ...partialDocumentField,
              type: fieldType.type,
              isMulti: Boolean(fieldType.is_multi),
              value: typeof value !== 'string' ? '' : value,
            }
          case 'NUMERIC':
            return {
              ...partialDocumentField,
              type: fieldType.type,
              value: typeof value !== 'number' ? 0 : value,
              isInteger: Boolean(fieldType.is_integer),
            }
          case 'DATE':
            return {
              ...partialDocumentField,
              type: fieldType.type,
              value: maybeParseDate(
                typeof value === 'string' ? `${value}T00:00:00` : null,
              )!,
            }
          default:
            return null
        }
      })
      .filter((field): field is DocumentField => isNotNil(field))
  }

  parse(json: Partial<DocumentDTO>): Partial<Document> {
    if (json.accesses) {
      json.accesses.forEach((access) => {
        this.primeDocumentAccess(access)
      })
    }
    this.primeDocumentAccess(json.access)
    if (json.history) {
      json.history.forEach((date) => {
        this.primeDocumentVersion(date)
      })
    }
    // If we at least have a latest version, we can prime it.
    if (!json.history && json.latest_version) {
      this.primeDocumentVersion(json.latest_version)
    }
    // const latest = this.primeDocumentVersion(json.latest_version)
    return {
      id: json.id,
      title: json.title,
      canEdit: json.can_edit,
      description: json.description,
      workspaceId: json.workspace_id,
      archived: json.archived,
      ephemeral: json.ephemeral,
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      owner: this.primeMember({
        user_id: json.owner_id,
        user_public_id: json.owner_public_id,
        workspace_id: json.workspace_id || this.workspaceId,
      }),
      documentType:
        json.document_type && this.primeDocumentType(json.document_type),
      _tagIds:
        json.tags &&
        compact(
          json.tags.map((t) => {
            const tag = this.primeDocumentTag(t)
            return tag ? tag.id : undefined
          }),
        ),
      workspace: this.primeWorkspace({
        id: json.workspace_id,
      }),
    }
  }
}

function reduceDocumentTypeFields(
  fields: Array<DocumentTypeField>,
  accumulator: Record<string, DocumentTypeField>,
) {
  fields.forEach((field) => {
    accumulator[field.id] = field

    if (field.type === 'ARRAY' || field.type === 'OBJECT') {
      reduceDocumentTypeFields(field.fields, accumulator)
    }
  })
}

export interface UploadProgress {
  uploaded: number
  percent: number
  total: number
}

const lazyNone = once(
  () =>
    new Document(
      () => Member.none,
      () => Member.none,
      () => undefined,
      () => undefined,
      () => undefined,
      () => undefined,
      () => undefined,
      () => [],
      () => [],
      () => undefined,
    ),
)

export type DocumentField = {
  id: string
  schemaId: string
  fileId: string
  corrected: boolean
  path: string
} & (
  | DocumentTextField
  | DocumentBooleanField
  | DocumentNumericField
  | DocumentDateField
)

interface DocumentTextField {
  type: 'TEXT'
  value: string
  isMulti: boolean
}

interface DocumentBooleanField {
  type: 'BOOL'
  value: boolean
}

interface DocumentNumericField {
  type: 'NUMERIC'
  value: number
  isInteger: boolean
}

interface DocumentDateField {
  type: 'DATE'
  value: Date
}
